<template>
  <transition name="fade" appear>
    <div class="login-page">
      <form
        @submit.prevent="handleLogin"
        v-if="!isLoading && !showPasswordReset"
      >
        <div class="logo-container">
          <img
            src="/assets/images/cenextra-logo.webp"
            alt="Cenextra Logo"
            class="login-logo"
          />
        </div>
        <div class="error" v-if="error">
          {{ error }}
        </div>
        <h6>Email or username</h6>
        <input
          type="text"
          v-model="email"
          placeholder="Email or username"
          ref="loginInput"
        />
        <h6>Password</h6>
        <input type="password" v-model="password" />
        <button type="submit" class="logout pixel-border">
          <span class="pixel"> Login </span>
        </button>
        <div class="forgot-password">
          <button
            type="button"
            @click="showPasswordReset = true"
            class="link-button"
          >
            Forgot Password?
          </button>
        </div>
      </form>

      <form
        @submit.prevent="handlePasswordReset"
        v-if="!isLoading && showPasswordReset"
      >
        <h3>Reset Password</h3>
        <div
          class="error"
          v-if="passwordResetErrors.general || passwordResetErrors.email"
        >
          {{ passwordResetErrors.general || passwordResetErrors.email }}
        </div>
        <div class="success" v-if="passwordResetSuccess">
          {{ passwordResetSuccess }}
        </div>
        <h6>Email</h6>
        <input
          type="email"
          v-model="resetEmail"
          placeholder="Enter your email address"
          required
        />
        <button type="submit" class="logout pixel-border">
          <span class="pixel"> Send Reset Link </span>
        </button>
        <div class="back-to-login">
          <button type="button" @click="backToLogin" class="link-button">
            Back to Login
          </button>
        </div>
      </form>

      <div class="loader-general" v-else-if="isLoading">
        <h3>Logging you in</h3>
        <svg
          version="1.1"
          id="L5"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          x="0px"
          y="0px"
          viewBox="0 0 100 100"
          enable-background="new 0 0 0 0"
          xml:space="preserve"
        >
          <circle fill="#000" stroke="none" cx="6" cy="50" r="6">
            <animateTransform
              attributeName="transform"
              dur="1s"
              type="translate"
              values="0 15 ; 0 -15; 0 15"
              repeatCount="indefinite"
              begin="0.1"
            />
          </circle>
          <circle fill="#000" stroke="none" cx="30" cy="50" r="6">
            <animateTransform
              attributeName="transform"
              dur="1s"
              type="translate"
              values="0 10 ; 0 -10; 0 10"
              repeatCount="indefinite"
              begin="0.2"
            />
          </circle>
          <circle fill="#000" stroke="none" cx="54" cy="50" r="6">
            <animateTransform
              attributeName="transform"
              dur="1s"
              type="translate"
              values="0 5 ; 0 -5; 0 5"
              repeatCount="indefinite"
              begin="0.3"
            />
          </circle>
        </svg>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { useGameAudioStore } from '@/stores/gameAudioStore';
import { usePasswordReset } from '@/composables/usePasswordReset';

const SYSTEM_AUDIO = '/assets/games/audio';

const router = useRouter();
const userStore = useUserStore();
const {
  requestReset,
  errors: passwordResetErrors,
  successMessage: passwordResetSuccess,
  isLoading: passwordResetLoading,
} = usePasswordReset();

const email = ref('');
const password = ref('');
const error = ref('');
const isLoading = ref(false);
const loginInput = ref(null);

// Password reset state
const showPasswordReset = ref(false);
const resetEmail = ref('');

const handleLogin = async () => {
  error.value = '';
  isLoading.value = true;
  const audioStore = useGameAudioStore();
  await audioStore.handleUserInteraction();
  audioStore.playSpeech(`${SYSTEM_AUDIO}/system-logging-you-in.mp3`);
  try {
    const loginResult = await userStore.login(email.value, password.value);

    if (loginResult === true) {
      console.log('Login successful, navigating to dashboard');
      await router.push('/');
    } else if (typeof loginResult === 'object' && !loginResult.success) {
      // Login failed with specific error message
      error.value = loginResult.message;
      audioStore.playSpeech(
        `${SYSTEM_AUDIO}/system-invalid-email-or-passwor.mp3`
      );
    } else {
      // Login failed with no specific message
      error.value = 'Invalid email or password. Please try again.';
      audioStore.playSpeech(
        `${SYSTEM_AUDIO}/system-invalid-email-or-passwor.mp3`
      );
    }
  } catch (err) {
    error.value =
      'There was a problem logging in. Check your login details and try again.';
    audioStore.playSpeech(
      `${SYSTEM_AUDIO}/system-there-was-a-problem-logg.mp3`
    );
    console.error('Login error:', err);
  } finally {
    isLoading.value = false;
  }
};

const handlePasswordReset = async () => {
  const result = await requestReset(resetEmail.value);
  if (result.success) {
    resetEmail.value = '';
  }
};

const backToLogin = () => {
  showPasswordReset.value = false;
  resetEmail.value = '';
  // Clear any password reset messages
  passwordResetErrors.general = '';
  passwordResetErrors.email = '';
};

onMounted(() => {
  if (loginInput.value) {
    loginInput.value.focus();
  }
});
</script>

<style scoped>
.login-page {
  max-width: 300px;
  margin: 0 auto;
  padding: 20px;
}

.logo-container {
  width: 50%;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  text-align: center;
  margin-bottom: 2rem;
}

.login-logo {
  /*max-width: 100%;*/
  height: auto;
  width: 500px;
  margin: 0 auto;
}

.error {
  color: red;
  margin-bottom: 15px;
}

.success {
  color: green;
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f0f8f0;
  border: 1px solid #4caf50;
  border-radius: 4px;
}

.loader-general {
  text-align: center;
}

.forgot-password,
.back-to-login {
  text-align: center;
  margin-top: 25px;
}

.link-button {
  background: none;
  border: none;
  color: #800000;
  text-decoration: none;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  position: relative;
  transition: all 0.3s ease;
}

.link-button::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 1px;
  background-color: #600000;
  transition: width 0.3s ease;
}

.link-button:hover {
  color: #600000;
  transform: translateY(-3px);
}

.link-button:hover::after {
  width: 100%;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
